import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";

import App from "./App";
import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/context/AuthContext";
import { PropertyProvider } from "@/context/PropertyContext";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { AuthFallbackBanner } from "@/components/AuthFallbackBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import "./index.css";

// Error monitoring (Sentry) — only active once VITE_SENTRY_DSN is set, so this
// is a complete no-op until you create a Sentry project and add the DSN as a
// Vercel env var. Uses Sentry's version-agnostic Loader Script (no npm
// dependency); the public key is the user portion of the DSN.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  const publicKey = SENTRY_DSN.split("//")[1]?.split("@")[0]?.split(":")[0];
  // The loader CDN host is region-specific: EU (de) → js-de.sentry-cdn.com,
  // US → js.sentry-cdn.com. Derive it from the DSN's ingest host so the right
  // regional loader is used (a mismatch loads a no-op stub).
  const ingestHost = SENTRY_DSN.split("@")[1]?.split("/")[0] ?? "";
  const region = /ingest\.([a-z]{2})\.sentry\.io/i.exec(ingestHost)?.[1]?.toLowerCase();
  const cdnHost = region && region !== "us" ? `js-${region}.sentry-cdn.com` : "js.sentry-cdn.com";
  if (publicKey) {
    const s = document.createElement("script");
    s.src = `https://${cdnHost}/${publicKey}.min.js`;
    s.crossOrigin = "anonymous";
    document.head.appendChild(s);
  }
}

// Fix: black screen on tab switch — when user returns to the tab,
// force the root element to repaint by toggling visibility briefly.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    const root = document.getElementById("root");
    if (root) {
      root.style.opacity = "0.99";
      requestAnimationFrame(() => {
        root.style.opacity = "1";
      });
    }
  }
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PropertyProvider>
              <AuthFallbackBanner />
              <App />
              <PWAUpdatePrompt />
            </PropertyProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
